import React, { useMemo } from 'react';
import { colors } from '../../styles/tokens';
import type { TiendaPorHora } from '../../types/indicadores.types';
import { buildChartGeometry } from '../../utils/chart';
import { useEsMovil } from '../../hooks/useEsMovil';

/**
 * Tamaño de fuente DENTRO del viewBox, sólo en móvil.
 *
 * POR QUÉ: el SVG mide 760 unidades de ancho y en un teléfono de 390 px se
 * pinta sobre ~340 px reales, así que cada unidad del viewBox vale 0,45 px.
 * Las fuentes de escritorio (10 unidades) quedaban en **4,5 px reales**:
 * ilegibles. 26 unidades devuelven ~11,6 px reales, el mínimo de una etiqueta.
 *
 * La geometría NO se toca —sigue viniendo de `utils/chart.ts`, que es la que
 * prueban los tests— : sólo cambian el tamaño del texto, dónde se ancla y
 * cuántas marcas se pintan.
 *
 * ⚠️ El corte de `useEsMovil` son 760 px, así que en el extremo alto del rango
 * (tableta angosta) estas etiquetas se ven grandes. Es deliberado: el caso a
 * arreglar es el teléfono, y grande-y-legible es mejor que chico-e-ilegible.
 */
const FUENTE_MOVIL = 26;

/**
 * Separación entre una etiqueta del eje Y y su línea de rejilla, en unidades
 * del viewBox. En escritorio son 4 (`ChartGridLine.ty`); con una fuente de 26
 * hace falta más aire o el texto se monta sobre la línea.
 */
const AIRE_ETIQUETA_MOVIL = 7;

export interface GraficoHoraProps {
  /** Serie `porHora` de `GET /dashboard/tienda` (horas 10..21, huecos en 0). */
  porHora: TiendaPorHora[];
  /** Hora actual (0..23) en `America/Mexico_City` — ver `getNowHourMx`. */
  nowHour: number;
  /**
   * `false` cuando la vista Mi tienda tiene un rango de fecha elegido (PT8,
   * `FechaTienda`): el marcador vertical punteado "ahora" solo tiene sentido
   * en modo vivo (rango=null), así que se oculta y el título cambia de "de
   * hoy" a "del periodo". Default `true` (comportamiento previo, sin
   * cambios).
   */
  mostrarAhora?: boolean;
}

/**
 * Gráfico "Comportamiento de hoy · por hora" (Mi tienda): recreado
 * pixel-exact de `Tablero Colectivo Moda.dc.html` (líneas 193-213), con 4
 * series sobre el mismo recorte por `nowHour`: **Consultas** (área + línea
 * gris) y **Atendidas** (verde, con puntos) en el eje primario, más
 * **Negados** (rojo, eje primario — mismo conteo comparable) y **Venta
 * (pares)** (azul, con puntos, **eje Y secundario propio** a la derecha)
 * que llegan ya calculadas por `utils/chart.ts#buildChartGeometry` (Task C1).
 *
 * Las horas futuras (después de la hora MX actual) se sombrean con un rect
 * (`#f4f6fa`) y NO se grafican — las líneas/área se cortan en `nowHour`, con
 * un marcador vertical punteado rojo "ahora" en ese punto. Toda la geometría
 * (escalas, paths, ticks) se calcula en `utils/chart.ts#buildChartGeometry`
 * para poder testearla sin montar el SVG.
 */
const GraficoHora: React.FC<GraficoHoraProps> = ({ porHora, nowHour, mostrarAhora = true }) => {
  const esMovil = useEsMovil();
  // En modo periodo (rango histórico, `mostrarAhora=false`) NO hay "ahora" que
  // recortar: toda la serie del periodo ya pasó. Se pasa una hora efectiva 24
  // para que `buildChartGeometry` grafique TODAS las horas (el rect "futuro"
  // queda de ancho 0). En modo vivo se usa la hora MX real, como siempre.
  const effectiveNowHour = mostrarAhora ? nowHour : 24;
  const chart = useMemo(
    () => buildChartGeometry(porHora, effectiveNowHour),
    [porHora, effectiveNowHour],
  );

  const fuenteEje = esMovil ? FUENTE_MOVIL : 10;

  /**
   * Rejilla del eje Y: en móvil se pinta UNA DE CADA DOS (0 %, 50 %, 100 %).
   * Las cinco líneas del escritorio caen a 22 px reales de separación en un
   * teléfono y las etiquetas, ya legibles, se pisarían entre sí.
   * Se conserva el índice original para saber cuál es la de arriba.
   */
  const rejillaY = chart.gridY
    .map((g, i) => ({ g, i }))
    .filter(({ i }) => !esMovil || i % 2 === 0);

  /** Eje secundario (Venta en pares): en móvil sólo el 0 y el tope. */
  const rejillaY2 = chart.gridY2
    .map((g, i) => ({ g, i }))
    .filter(({ i }) => !esMovil || i === 0 || i === chart.gridY2.length - 1);

  /**
   * En móvil las 12 horas del eje X quedan a 28 px reales una de otra: se pinta
   * una de cada tres (más la última, que es el cierre de la jornada).
   */
  const pasoTicks = esMovil ? Math.max(1, Math.ceil(chart.xticks.length / 5)) : 1;

  /**
   * Y de la etiqueta del eje Y en móvil: por ENCIMA de su línea, salvo la de
   * arriba del todo —que se saldría del viewBox— que va por debajo.
   * En escritorio se respeta el `ty` que calcula `buildChartGeometry`.
   */
  const yEtiqueta = (g: { y: number; ty: number }, esTope: boolean) => {
    if (!esMovil) return g.ty;
    return esTope ? g.y + AIRE_ETIQUETA_MOVIL + FUENTE_MOVIL * 0.6 : g.y - AIRE_ETIQUETA_MOVIL;
  };

  // La etiqueta "ahora" se ancla al lado de su línea punteada en vez de
  // centrarse, cuando la línea está pegada a un borde: centrada y a 26
  // unidades, se cortaría contra el filo del viewBox.
  const anclaAhora: 'start' | 'middle' | 'end' = !esMovil
    ? 'middle'
    : chart.nowX > 660
      ? 'end'
      : chart.nowX < 100
        ? 'start'
        : 'middle';
  const xAhora =
    anclaAhora === 'end' ? chart.nowX - 6 : anclaAhora === 'start' ? chart.nowX + 6 : chart.nowX;

  return (
    <div
      data-testid="grafico-hora"
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        // Márgenes laterales más angostos en móvil: cada píxel de padding se lo
        // quita al ancho del gráfico, que es lo único que da legibilidad aquí.
        padding: esMovil ? '14px 12px 8px' : '16px 18px 10px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05), 0 18px 40px -30px rgba(20,28,54,.5)',
      }}
    >
      <div
        style={{
          display: 'flex',
          // En móvil el título y la leyenda no caben en la misma fila de 340 px:
          // se apilan en vez de comprimirse hasta salirse de la pantalla.
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'stretch' : 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: esMovil ? 8 : 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            // Título y subtítulo, uno debajo del otro: en una fila el subtítulo
            // ("Demanda vs. atención…") empujaba el bloque más allá del ancho.
            flexDirection: esMovil ? 'column' : 'row',
            alignItems: esMovil ? 'flex-start' : 'baseline',
            gap: esMovil ? 2 : 10,
            // Sin esto el flex se niega a encoger el bloque de texto. Sólo en
            // móvil: en escritorio cambiaría el reparto actual de la fila.
            minWidth: esMovil ? 0 : undefined,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 800 }}>
            Comportamiento {mostrarAhora ? 'de hoy' : 'del periodo'} · por hora
          </h3>
          <span style={{ fontSize: 11.5, color: colors.texto.quaternary, fontWeight: 600 }}>
            Demanda vs. atención a lo largo del día
          </span>
        </div>
        {/* Las 4 series de la leyenda ocupan ~430 px: en móvil se dejan
            envolver en dos renglones en vez de desbordar la tarjeta. */}
        <div
          style={{
            display: 'flex',
            gap: esMovil ? 10 : 16,
            flexWrap: esMovil ? 'wrap' : undefined,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: colors.texto.secondary,
            }}
          >
            <span style={{ width: 11, height: 11, borderRadius: 3, background: colors.consultasBar }} />
            Consultas
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: colors.texto.secondary,
            }}
          >
            <span style={{ width: 14, height: 3, borderRadius: 2, background: colors.verde }} />
            {/* Rótulo del glosario (12-oct), el mismo del embudo y la app. */}
            Solicitudes atendidas
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: colors.texto.secondary,
            }}
          >
            <span style={{ width: 14, height: 3, borderRadius: 2, background: colors.rojo }} />
            Negados
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: colors.texto.secondary,
            }}
          >
            <span style={{ width: 14, height: 3, borderRadius: 2, background: colors.azulClaro }} />
            Venta (pares)
          </span>
        </div>
      </div>

      <svg
        viewBox="0 0 760 250"
        style={{ width: '100%', height: 'auto', display: 'block', marginTop: 6 }}
        fontFamily="Manrope"
      >
        <rect x={chart.futureX} y={16} width={chart.futureW} height={206} fill={colors.panelBg} />

        {rejillaY.map(({ g, i }) => (
          <React.Fragment key={i}>
            <line x1={34} y1={g.y} x2={744} y2={g.y} stroke={colors.ringTrack} strokeWidth={1} />
            {/* En móvil la etiqueta se mete DENTRO del área de gráfico (x=38,
                anclada al inicio): a 26 unidades ya no cabe en los 34 del
                margen izquierdo y se cortaría contra el filo del viewBox. */}
            <text
              x={esMovil ? 38 : 30}
              y={yEtiqueta(g, i === chart.gridY.length - 1)}
              textAnchor={esMovil ? 'start' : 'end'}
              fontSize={fuenteEje}
              fill={colors.iconMuted}
              fontWeight={600}
            >
              {g.label}
            </text>
          </React.Fragment>
        ))}

        <path d={chart.areaPath} fill={colors.areaFill} opacity={0.55} />
        <path
          d={chart.consPath}
          fill="none"
          stroke={colors.consultasBar}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={chart.atendPath}
          fill="none"
          stroke={colors.verde}
          strokeWidth={2.6}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {chart.dots.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3.1} fill="#fff" stroke={colors.verde} strokeWidth={2} />
        ))}

        {/* Negados (rojo) — eje primario, mismo recorte por `pastP` que Consultas/Atendidas. */}
        <g data-testid="serie-negados">
          <path
            d={chart.negadosPath}
            fill="none"
            stroke={colors.rojo}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {chart.negadosDots.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={2.6} fill="#fff" stroke={colors.rojo} strokeWidth={1.6} />
          ))}
        </g>

        {/* Venta en pares (azul) — eje Y SECUNDARIO propio (`gridY2`/`maxVenta`), no comparable
            al conteo de Consultas/Atendidas/Negados. Puede traer huecos (horas con `venta: null`
            si Proscai cayó): `ventaPath` ya viene cortado en subpaths desde `buildChartGeometry`. */}
        <g data-testid="serie-venta">
          <path
            d={chart.ventaPath}
            fill="none"
            stroke={colors.azulClaro}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {chart.ventaDots.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={3.1} fill="#fff" stroke={colors.azulClaro} strokeWidth={2} />
          ))}
        </g>

        {/* Eje Y secundario (Venta en pares), a la derecha del plot (x >= 744): solo ticks +
            labels tenues (sin gridlines completas, para no saturar el gráfico con 2 escalas). */}
        <g data-testid="eje-venta">
          {rejillaY2.map(({ g, i }) => (
            <React.Fragment key={i}>
              <line x1={744} y1={g.y} x2={749} y2={g.y} stroke={colors.azulClaro} strokeWidth={1} opacity={0.4} />
              {/* Misma razón que el eje primario: en móvil la etiqueta entra al
                  área de gráfico (anclada al final en x=740) porque los 16
                  puntos de margen derecho no dan para 26 unidades de texto. */}
              <text
                x={esMovil ? 740 : 752}
                y={esMovil ? yEtiqueta(g, i === chart.gridY2.length - 1) : g.ty}
                textAnchor={esMovil ? 'end' : 'start'}
                fontSize={esMovil ? FUENTE_MOVIL : 9}
                fill={colors.azulClaro}
                fontWeight={600}
                opacity={esMovil ? 0.85 : 0.65}
              >
                {g.label}
              </text>
            </React.Fragment>
          ))}
          {/* El rótulo "pares" del eje secundario se omite en móvil: no hay
              margen derecho donde ponerlo a tamaño legible y la leyenda de
              arriba ya dice "Venta (pares)" en el mismo azul. */}
          {!esMovil && (
            <text x={752} y={14} textAnchor="start" fontSize={8.5} fill={colors.azulClaro} fontWeight={700} opacity={0.7}>
              pares
            </text>
          )}
        </g>

        {mostrarAhora && (
          <>
            <line
              x1={chart.nowX}
              y1={12}
              x2={chart.nowX}
              y2={222}
              stroke={colors.rojo}
              strokeWidth={1.4}
              strokeDasharray="4 4"
            />
            {/* En móvil "ahora" baja al interior del gráfico (y=62): a 26
                unidades, sobre la línea superior del viewBox se cortaría, y a
                la altura de la primera rejilla chocaría con su etiqueta. */}
            <text
              x={xAhora}
              y={esMovil ? 62 : 10}
              textAnchor={anclaAhora}
              fontSize={fuenteEje}
              fill={colors.rojo}
              fontWeight={800}
            >
              ahora
            </text>
          </>
        )}

        {chart.xticks.map((t, i) =>
          !esMovil || i % pasoTicks === 0 || i === chart.xticks.length - 1 ? (
            <text
              key={i}
              x={t.x}
              // 4 unidades más abajo en móvil: con la fuente grande, la hora
              // llegaba a tocar la línea inferior de la rejilla.
              y={esMovil ? 244 : 240}
              textAnchor="middle"
              fontSize={fuenteEje}
              fill={colors.texto.quaternary}
              fontWeight={600}
            >
              {t.label}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
};

export default GraficoHora;
